import React, { useEffect, useState } from "react";
import { PulseLoader } from "react-spinners";
import { AlertTriangle } from "lucide-react";

interface ConfirmDeleteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  /** The user's full name (or fallback display name) shown in the body. */
  userName?: string | null;
  /** The user's email shown alongside the name for extra verification. */
  userEmail?: string | null;
  /**
   * The username the admin must type into the input to enable the delete
   * button. This is a deliberate safety gate for an irreversible action.
   */
  usernameToConfirm?: string | null;
  /** e.g. "Buyer", "Vendor" — used in copy. Defaults to "User". */
  userTypeLabel?: string;
}

const ConfirmDeleteUserModal: React.FC<ConfirmDeleteUserModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
  userName,
  userEmail,
  usernameToConfirm,
  userTypeLabel = "User",
}) => {
  const [typedUsername, setTypedUsername] = useState("");

  // Reset the input every time the modal is reopened so a stale value
  // never leaves the destructive button armed.
  useEffect(() => {
    if (isOpen) setTypedUsername("");
  }, [isOpen]);

  if (!isOpen) return null;

  const displayName = userName?.trim() || `this ${userTypeLabel.toLowerCase()}`;
  const requiresTypedConfirmation = Boolean(usernameToConfirm?.trim());
  const isConfirmationMatched = requiresTypedConfirmation
    ? typedUsername.trim() === usernameToConfirm?.trim()
    : true;
  const isDeleteDisabled = loading || !isConfirmationMatched;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white w-full max-w-md rounded-lg p-6 shadow-lg mx-4">
        <div className="flex justify-center mb-4">
          <div className="bg-red-100 text-red-600 rounded-full p-4">
            <AlertTriangle size={28} />
          </div>
        </div>

        <h2 className="text-center text-xl font-semibold mb-2">
          Delete {userTypeLabel}
        </h2>

        <p className="text-center text-gray-600 mb-2">
          You are about to permanently delete{" "}
          <span className="font-medium text-gray-900">{displayName}</span>
          {userEmail ? (
            <>
              {" "}
              (<span className="text-gray-700">{userEmail}</span>)
            </>
          ) : null}
          .
        </p>
        <p className="text-center text-sm text-red-600 mb-5">
          This action is irreversible. The account and its associated data will
          be removed.
        </p>

        {requiresTypedConfirmation && (
          <div className="mb-5">
            <label
              htmlFor="confirm-username"
              className="block text-sm text-gray-700 mb-2"
            >
              To confirm, type{" "}
              <span className="font-mono font-semibold text-gray-900">
                {usernameToConfirm}
              </span>{" "}
              below.
            </label>
            <input
              id="confirm-username"
              type="text"
              autoComplete="off"
              autoFocus
              value={typedUsername}
              onChange={(e) => setTypedUsername(e.target.value)}
              disabled={loading}
              placeholder={usernameToConfirm ?? ""}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-60"
            />
          </div>
        )}

        <div className="flex justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="w-1/2 bg-gray-100 text-gray-800 rounded-md py-2 px-4 hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleteDisabled}
            className="w-1/2 bg-red-600 text-white rounded-md py-2 px-4 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <PulseLoader size={6} color="#fff" />
            ) : (
              `Yes, Delete ${userTypeLabel}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDeleteUserModal;
